import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AmbientThreads from './AmbientThreads';
export const features = [{
  id: 1,
  name: 'Reframe',
  eyebrow: 'Before you send',
  subheading: "Don't let one text ruin everything.",
  bullets: ["Stop sending the paragraph you'll regret in 10 minutes", 'Say what hurt you without starting another fight', "Don't let one emotional text make everything worse"],
  previewImage: 'https://cdn.magicpatterns.com/uploads/1z6KizmJmBbDnsB3aJUWSQ/Reframe.png',
  comingSoon: false
}, {
  id: 2,
  name: 'Interpret',
  eyebrow: 'Before you spiral',
  subheading: 'Read the message without spiraling.',
  bullets: ['Stop rereading the same text over and over', "Stop always assuming there's something wrong", "Don't let one message ruin your whole day"],
  previewImage: 'https://cdn.magicpatterns.com/uploads/kFBnzgQaxJxZHs8h1rbdSD/Interpret.png',
  comingSoon: false
}, {
  id: 3,
  name: 'Create',
  eyebrow: 'From blank to ready',
  subheading: 'Make your words match the moment.',
  bullets: ['Stop staring at the screen not knowing what to say', 'Say what you mean without overthinking every word', 'Write captions, posts, and texts that actually land'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/owADfZH1EDpXkhM819yWV7/Create.png',
  comingSoon: false
}, {
  id: 4,
  name: 'Unpack',
  eyebrow: 'After it happens',
  subheading: 'When one conversation keeps living in your head.',
  bullets: ['Stop replaying every word over and over', 'Understand what they meant, not just how it felt', 'Figure out what actually happened before it eats at you'],
  previewImage: 'https://cdn.magicpatterns.com/uploads/c72Qekd7Kxzh9mFrXJUSUq/Unpack.png',
  comingSoon: false
}];
const AUTO_CYCLE_MS = 5000;
// Only auto-cycle through shipped features (non-comingSoon)
const autoCycleCount = features.filter(f => !f.comingSoon).length;
const FeatureCards = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [subSlide, setSubSlide] = useState(0);
  const [userInteracted, setUserInteracted] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<'left' | 'right'>('left');
  // Reset sub-carousel when switching features
  useEffect(() => {
    setSubSlide(0);
  }, [activeIndex]);
  const handleSubSlide = (i: number) => {
    setUserInteracted(true);
    setSubSlide(i);
  };
  useEffect(() => {
    const obs = new IntersectionObserver(entries => entries[0].isIntersecting && setVisible(true), {
      threshold: 0.1
    });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (userInteracted) return;
    const interval = setInterval(() => {
      setActiveIndex(i => (i + 1) % autoCycleCount);
    }, AUTO_CYCLE_MS);
    return () => clearInterval(interval);
  }, [userInteracted]);
  const handleTabClick = (i: number) => {
    setUserInteracted(true);
    setSwipeDirection(i > activeIndex ? 'left' : 'right');
    setActiveIndex(i);
  };
  const handleSwipe = (direction: 'left' | 'right') => {
    setUserInteracted(true);
    if (direction === 'left' && activeIndex < features.length - 1) {
      setSwipeDirection('left');
      setActiveIndex(activeIndex + 1);
    } else if (direction === 'right' && activeIndex > 0) {
      setSwipeDirection('right');
      setActiveIndex(activeIndex - 1);
    }
  };
  const active = features[activeIndex];
  const swipeVariants = {
    initial: (dir: 'left' | 'right') => ({
      opacity: 0,
      x: dir === 'left' ? 80 : -80
    }),
    animate: {
      opacity: 1,
      x: 0
    },
    exit: (dir: 'left' | 'right') => ({
      opacity: 0,
      x: dir === 'left' ? -80 : 80
    })
  };
  return <section id="features" ref={sectionRef} className="relative py-24 md:py-32" style={{
    backgroundColor: 'var(--cream)'
  }} data-id="element-60">
      <AmbientThreads variant="soft" data-id="element-61" />

      <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-10" data-id="element-62">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={visible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="max-w-2xl mb-12 md:mb-16" data-id="element-63">
          <p className="text-xs tracking-widest uppercase mb-4" style={{
          color: 'var(--coral)'
        }} data-id="element-64">
            Four ways to text better
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] tracking-tight" style={{
          color: 'var(--navy)',
          fontFamily: "'DM Serif Display', serif"
        }} data-id="element-65">
            A calmer layer{' '}
            <span className="italic" style={{
            color: 'var(--coral)'
          }} data-id="element-66">
              between you and your messages.
            </span>
          </h2>
        </motion.div>

        {/* Tabs */}
        <div role="tablist" aria-label="Feature tabs" className="flex flex-wrap gap-x-1 gap-y-2 mb-10 md:mb-12 border-b" style={{
        borderColor: 'var(--border-soft)'
      }} data-id="element-67">
          {features.map((f, i) => {
          const isActive = i === activeIndex;
          return <button key={f.id} role="tab" aria-selected={isActive} aria-controls={`feature-panel-${f.id}`} onClick={() => handleTabClick(i)} className="relative px-4 md:px-5 py-3 text-sm md:text-base transition-colors duration-300 inline-flex items-center gap-2" style={{
            color: isActive ? 'var(--navy)' : 'var(--muted)',
            fontFamily: "'DM Serif Display', serif"
          }} data-id="element-68">
                <span className="relative z-10" data-id="element-69">{f.name}</span>
                {f.comingSoon && <span className="relative z-10 text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded-full" style={{
              fontFamily: 'Inter, sans-serif',
              backgroundColor: 'var(--peach)',
              color: 'var(--coral)'
            }} data-id="element-70">
                    Soon
                  </span>}
                {isActive && <motion.span layoutId="feature-tab-indicator" className="absolute left-0 right-0 -bottom-px h-0.5" style={{
              backgroundColor: 'var(--coral)'
            }} transition={{
              duration: 0.5,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-71" />}
              </button>;
        })}
        </div>

        {/* Mobile swipe dots */}
        <div className="flex md:hidden justify-center gap-2 mb-4" data-id="element-72">
          {features.map((_, i) => <button key={i} onClick={() => handleTabClick(i)} className="w-2 h-2 rounded-full transition-all duration-300" style={{
          backgroundColor: i === activeIndex ? 'var(--coral)' : 'var(--border-soft)',
          transform: i === activeIndex ? 'scale(1.3)' : 'scale(1)'
        }} aria-label={`Go to feature ${i + 1}`} data-id="element-73" />)}
        </div>

        {/* Active panel */}
        <div className="relative touch-pan-y" data-id="element-74">
          <AnimatePresence mode="wait" custom={swipeDirection} data-id="element-75">
            <motion.div key={active.id} id={`feature-panel-${active.id}`} role="tabpanel" custom={swipeDirection} variants={swipeVariants} initial="initial" animate="animate" exit="exit" transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1]
          }} drag="x" dragConstraints={{
            left: 0,
            right: 0
          }} dragElastic={0.15} onDragEnd={(_, info) => {
            const swipeThreshold = 50;
            if (info.offset.x < -swipeThreshold) {
              handleSwipe('left');
            } else if (info.offset.x > swipeThreshold) {
              handleSwipe('right');
            }
          }} className="flex flex-col lg:flex-row gap-8 lg:gap-12 py-4 lg:py-6 cursor-grab active:cursor-grabbing md:cursor-default md:active:cursor-default" data-id="element-76">
              {/* Text column */}
              <div className="flex-1 flex flex-col justify-center min-w-0 order-2 lg:order-1" data-id="element-77">
                <p className="text-xs tracking-widest uppercase mb-3" style={{
                color: 'var(--coral)'
              }} data-id="element-78">
                  {active.eyebrow}
                </p>
                <h3 className="text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.05] mb-4" style={{
                color: 'var(--navy)',
                fontFamily: "'DM Serif Display', serif"
              }} data-id="element-79">
                  {active.name}
                </h3>
                <p className="text-lg md:text-xl leading-snug mb-6 italic" style={{
                color: 'var(--navy-soft)',
                fontFamily: "'DM Serif Display', serif"
              }} data-id="element-80">
                  {active.subheading}
                </p>
                <ul className="space-y-3" data-id="element-81">
                  {active.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-3" data-id="element-82">
                      <span className="flex-shrink-0 mt-2 w-1.5 h-1.5 rounded-full" style={{
                    backgroundColor: 'var(--coral)'
                  }} data-id="element-83" />
                      <span className="text-base leading-relaxed" style={{
                    color: 'var(--muted)'
                  }} data-id="element-84">
                        {bullet}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* Phone / placeholder column */}
              <div className="flex-shrink-0 flex justify-center items-center order-1 lg:order-2 lg:w-[280px]" data-id="element-85">
                <div className="relative w-[220px] md:w-[260px] lg:w-[280px] rounded-[32px] overflow-hidden flex items-center justify-center" style={{
                border: '7px solid var(--navy)',
                backgroundColor: active.comingSoon ? 'var(--cream-warm)' : 'var(--navy)',
                boxShadow: '0 30px 60px -15px rgba(26, 31, 54, 0.35), 0 18px 30px -12px rgba(26, 31, 54, 0.2)',
                aspectRatio: '9 / 19.5'
              }} data-id="element-86">
                  {active.comingSoon ? <div className="text-center px-6" data-id="element-87">
                      <p className="text-xs tracking-widest uppercase mb-3" style={{
                    color: 'var(--coral)'
                  }} data-id="element-88">
                        Coming soon
                      </p>
                      <p className="text-2xl md:text-3xl leading-tight" style={{
                    color: 'var(--navy)',
                    fontFamily: "'DM Serif Display', serif"
                  }} data-id="element-89">
                        {active.name}
                      </p>
                    </div> : <img src={active.previewImage as string} alt={`${active.name} preview`} className="w-full h-full object-cover" data-id="element-90" />}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>;
};
export default FeatureCards;